

























import UserRequired from "@/components/UserRequired";
import {Component, Inject, Watch} from "vue-property-decorator";
import RegreminderInfoDialog from "@/components/sio/RegreminderInfoDialog.vue";
import RegreminderProblemDialog from "@/components/sio/RegreminderProblemDialog.vue";
import EnableBackgroundLocationDlg from "@/components/sio/EnableBackgroundLocationDlg.vue";
import {BackgroundLocationPermission, GeofenceStatusCode, NotificationManagerStatus, RegReminderConfig} from "@/modules/models";
import delay from "@/modules/delay";
import {NativeApi} from "@/modules/NativeBridge";
import {MaxRegreminderLocationsPerUser} from "@/modules/constants";
import BaseApi from "@/store/modules/base";
import Api from "@/components/sio/Api";
import CommonBase from "@/components/CommonBase";

@Component({
  components: {
    "at-regreminder-info-dialog": RegreminderInfoDialog,
    "at-regreminder-problem-dialog": RegreminderProblemDialog,
    "at-enable-background-location": EnableBackgroundLocationDlg,
  }
})
export default class RegReminderEnablement extends UserRequired {
  @Inject()
  nativeApi!: NativeApi;
  @Inject()
  baseApi!: BaseApi;
  @Inject()
  api!: Api;

  loadingRegreminder = false;

  get activeSio() {
    return this.baseApi.activeSio;
  }

  get regreminder(): boolean {
    const sioEnabled = this.activeSio != null ? this.activeSio.regreminder : false;
    return this.regReminderConfigEnabled && sioEnabled;
  }
  set regreminder(v: boolean) {
    this.setRegreminder(v);
  }
  async setRegreminder(enabled: boolean) {
    try {
      this.loadingRegreminder = true;
      if(!this.regReminderConfigEnabled && enabled) {
        if(this.regReminderConfig == null) {
          this.regReminderConfig = RegReminderConfig.default();
        }
        const backgroundLocOk = await this.promptEnableBackgroundLocation();
        if(backgroundLocOk) {
          this.regReminderConfig.enabled = true;
        } else {
          this.emitSnack(`Tilgang til bakgrunnsposisjon avbrutt. Registreringspåminnelse blir ikke aktivert`, "warn");
        }
        await this.fixBackgroundPosition(); // Shows permissions prompt, or opens settings
      }
      if(this.activeSio != null) {
        const resp = await this.api.setRegreminder(this.activeSio.terminalLocation.id, enabled);
        if(resp.removedLocId != null) {
          this.emitSnack(`Registreringspåminnelse på ${resp.removedLocDescription} ble deaktivert. (Maks ${MaxRegreminderLocationsPerUser} anlegg kan være aktivert samtidig)`, "info");
        }
        await this.baseApi.loadActiveSio();

      }
    } catch (e) {
      this.emitError(e);
    } finally {
      this.loadingRegreminder = false;
    }
  }


  get regReminderConfigAvailable() {
    return this.nativeApi.available && this.nativeApi.atleastVersion(0, 3, 0);
  }
  regReminderConfig: RegReminderConfig | null = null;

  get regReminderConfigEnabled(): boolean {
    return this.regReminderConfig != null && this.regReminderConfig.enabled;
  }


  get regReminderConfigError(): string | null {
    if(!this.regReminderConfigEnabled) {
      return null;
    }
    return this.regReminderConfig && this.regReminderConfig.errorMessage ||
        this.notificationManagerStatus && this.notificationManagerStatus.regreminderNotificationError() ||
        null;
  }

  async loadRegReminderConfig() {
    if(this.regReminderConfigAvailable) {
      this.regReminderConfig = await this.nativeApi.getRegReminderConfig();
      if(this.regReminderConfig.statusCode == GeofenceStatusCode.Pending) {
        delay(10000).then(async () => {
          this.regReminderConfig = await this.nativeApi.getRegReminderConfig();
        })
      }
    }
  }

  async setRegReminderConfig() {
    if(this.regReminderConfig != null) {
      try {
        this.regReminderConfig = await this.nativeApi.setRegReminderConfig(this.regReminderConfig);
      } catch (e) {
        this.regReminderConfig.enabled = false;
        throw e;
      }
    }
  }

  backgroundLocationPermission = new BackgroundLocationPermission(false, 0);

  @Watch("regReminderConfig")
  async loadBackgroundLocationPermission() {
    const doLoad = this.nativeApi.atleastVersion(1, 3, 0);
    if(doLoad) {
      this.backgroundLocationPermission = await this.nativeApi.checkBackgroundLocationPermission();
    }
  }

  async fixBackgroundPosition() {
    if(this.backgroundLocationPermission.askAllowed()) {
      await this.setRegReminderConfig();
    } else {
      // User must manually adjust permission in settings
      console.debug(`User must manually adjust permission settings. Opening settings.`);
      const opened = await this.nativeApi.openAppSettings();
      if (opened) { // App settings was opened, and user returned to app
        await this.setRegReminderConfig() // Try activating again, will refresh the status.
      } else {
        this.emitSnack(`Åpning av app innstillinger feilet.`, "error");
      }
    }
  }

  notificationManagerStatus: NotificationManagerStatus | null = null;
  async loadNotificationManagerStatus() {
    this.notificationManagerStatus = await this.nativeApi.getNotificationManagerStatus();
  }
  async openNotificationSettings() {
    await this.nativeApi.openNotificationSettings();
    this.emitSnack(`Oppdater for å sjekke status på nytt`, "info");
    this.reloadNeeded = true;
  }

  reloadNeeded = false; // Set to true when an app setting or something that could fix regreminder problem may have been resolved, but we have no way of knowing except asking the user to reload it's state.

  showEnableBackgroundLocation = false;
  async promptEnableBackgroundLocation(): Promise<boolean> {
    const ref = this.$refs["EnableBackgroundLocationDlg"] as EnableBackgroundLocationDlg;
    const retPromise = CommonBase.oneEventPromise(ref, "at-enable-background-location").then(result => result === true);
    this.showEnableBackgroundLocation = true;
    const ret = await retPromise;
    this.showEnableBackgroundLocation = false;
    return ret;
  }

  async statusLoad() {
    await this.loadRegReminderConfig();
    await this.loadNotificationManagerStatus();
    this.reloadNeeded = false;
  }

  created() {
    this.statusLoad();
  }
}
